import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Plus, RefreshCw, Search, Download, X, ShieldAlert, AlertOctagon,
  ClipboardList, CheckCircle2, Activity, Filter, Eye, ChevronRight
} from "lucide-react";
import StatusPill from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import IncidentNewDialog from "@/components/incidents/IncidentNewDialog";
import MobileIncidentsList from "@/components/mobile/MobileIncidentsList";

const CARDS = [
  { key: "open", label: "Open Incidents", icon: AlertOctagon, tone: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", filter: { status: "reported" } },
  { key: "under_investigation", label: "Under Investigation", icon: Activity, tone: "text-sky-600", bg: "bg-sky-50", border: "border-sky-200", filter: { status: "under_investigation" } },
  { key: "safeguarding", label: "Safeguarding Concerns", icon: ShieldAlert, tone: "text-red-600", bg: "bg-red-50", border: "border-red-200", filter: { safeguarding: "yes" } },
  { key: "overdue_actions", label: "Overdue Actions", icon: ClipboardList, tone: "text-rose-600", bg: "bg-rose-50", border: "border-rose-200", filter: { overdue: true } },
  { key: "closed_this_month", label: "Closed This Month", icon: CheckCircle2, tone: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", filter: { closed_this_month: true } },
];

const CATEGORIES = ["accident", "safeguarding", "near_miss", "behaviour", "injury", "fall", "medication", "other"];
const SEVERITIES = ["low", "moderate", "high", "critical"];
const STATUSES = ["reported", "manager_review", "under_investigation", "actions_required", "awaiting_closure", "closed"];
const SAFEGUARDING = ["yes", "no", "under_review", "not_applicable"];

function fmtDate(v) {
  if (!v) return "—";
  return String(v).slice(0, 16).replace("T", " ");
}

function humanize(v) {
  if (!v) return "—";
  return String(v).replace(/_/g, " ");
}

function Card({ card, value, active, onClick }) {
  const Icon = card.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`card-${card.key}`}
      className={`text-left rounded-lg border p-3 transition ${card.bg} ${card.border} ${active ? "ring-2 ring-sky-500" : "hover:shadow-sm"}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest text-slate-600 font-semibold">{card.label}</span>
        <Icon className={`w-4 h-4 ${card.tone}`} />
      </div>
      <div className="text-2xl font-heading font-semibold text-slate-900 tabular-nums" data-testid={`card-${card.key}-value`}>{value ?? "—"}</div>
    </button>
  );
}

function QuickPanel({ incident, onClose, onViewFull }) {
  if (!incident) return null;
  return (
    <aside className="w-96 shrink-0 border-l border-slate-200 bg-slate-50 overflow-y-auto" data-testid="incident-quick-panel">
      <div className="p-4 border-b border-slate-200 bg-white flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Incident</div>
          <div className="font-heading text-sm font-semibold text-slate-900 mt-0.5">
            {incident.reference} · {humanize(incident.category)}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 mono">{fmtDate(incident.occurred_at)}</div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="btn-close-quick"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-4 space-y-3 text-sm">
        <Row label="Service User" value={incident.client_name || "—"} />
        <Row label="Location" value={incident.location || "—"} />
        <Row label="Severity"><StatusPill value={incident.severity} kind="severity" /></Row>
        <Row label="Safeguarding"><StatusPill value={incident.safeguarding_status} kind="generic" /></Row>
        <Row label="Reported by" value={incident.reported_by || incident.created_by_name} />
        <Row label="Status"><StatusPill value={incident.status} kind="generic" /></Row>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Description</div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap">{incident.description || "—"}</div>
        </div>
        {incident.immediate_action && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Immediate action</div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap">{incident.immediate_action}</div>
          </div>
        )}
        <div className="text-[11px] text-slate-500">
          {(incident.actions || []).length} corrective action(s) · {(incident.witnesses || []).length} witness(es)
        </div>
        <Button
          size="sm" className="w-full bg-sky-700 hover:bg-sky-800"
          onClick={onViewFull} data-testid="btn-view-full-incident"
        >
          View Full Incident <ChevronRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>
    </aside>
  );
}

function Row({ label, value, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-800">{children ?? value ?? "—"}</div>
    </div>
  );
}

export default function Incidents() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [filters, setFilters] = useState({
    date_from: "", date_to: "", site_id: "", category: "",
    severity: "", status: "", safeguarding: "", client_id: "",
  });
  const [cardFilters, setCardFilters] = useState({});
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);

  const canManage = ["admin", "manager", "registered_manager", "deputy_manager", "service_manager", "super_admin"].includes(user?.role);

  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get("/incidents/summary");
      setSummary(r.data);
    } catch (e) { /* silent */ }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, ...cardFilters };
      if (query.trim()) params.search = query.trim();
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const r = await api.get("/incidents", { params });
      setRows(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters, cardFilters, query]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadRows(); }, [loadRows]);

  // load sites + clients once (best-effort — some roles may 403 on sites)
  useEffect(() => {
    api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {});
    api.get("/clients").then((r) => setClients(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => {});
  }, []);

  const handleCardClick = (card) => {
    if (activeCard === card.key) {
      setActiveCard(null);
      setCardFilters({});
    } else {
      setActiveCard(card.key);
      setCardFilters(card.filter);
    }
  };

  const clearFilters = () => {
    setFilters({ date_from: "", date_to: "", site_id: "", category: "", severity: "", status: "", safeguarding: "", client_id: "" });
    setCardFilters({});
    setActiveCard(null);
    setQuery("");
  };

  const handleExportPdf = async () => {
    try {
      await downloadBlob(`/reports/module/incidents`, `ayobell-incidents.pdf`);
      toast.success("PDF downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = rows;
  const activeFilterCount = Object.values({ ...filters, ...cardFilters }).filter(Boolean).length + (query ? 1 : 0);

  const siteById = useMemo(() => {
    const map = {};
    sites.forEach((s) => { if (s?.id) map[s.id] = s.name; });
    return map;
  }, [sites]);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Batch 5.1 · MOBILE / TABLET view ( <lg ) — card list. Reuses the
          SAME state, filters, handlers and endpoints as desktop. Card tap
          navigates to the existing `/incidents/{id}` route — no mobile
          mutation surface, no new backend call. */}
      <MobileIncidentsList
        loading={loading}
        rows={filtered}
        summary={summary}
        siteById={siteById}
        activeCard={activeCard}
        activeFilterCount={activeFilterCount}
        query={query}
        onQueryChange={setQuery}
        onOpenFilters={() => setShowFilters((v) => !v)}
        onRefresh={() => { loadSummary(); loadRows(); }}
        onExport={handleExportPdf}
        onNewRecord={() => setNewOpen(true)}
        onCardClick={(k) => {
          const c = CARDS.find((x) => x.key === k);
          if (c) handleCardClick(c);
        }}
        canManage={canManage}
      />

      {/* Desktop toolbar ( ≥ lg ) — unchanged from before Batch 5.1. */}
      <div className="h-14 border-b border-slate-200 bg-white px-6 hidden lg:flex items-center justify-between shrink-0">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Clinical</div>
          <h1 className="font-heading text-base font-semibold" data-testid="page-title">Incidents &amp; Safeguarding</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search reference, description, service user…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="incidents-search"
              className="w-72 h-9 pl-8 text-sm bg-white"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => { loadSummary(); loadRows(); }} data-testid="btn-refresh" className="h-9 text-slate-700">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowFilters((v) => !v)} data-testid="btn-toggle-filters" className={`h-9 text-slate-700 ${showFilters ? "bg-slate-100" : ""}`}>
            <Filter className="w-3.5 h-3.5 mr-1.5" /> Filters {activeFilterCount > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-sky-100 text-sky-800">{activeFilterCount}</span>}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportPdf} data-testid="btn-export-pdf" className="h-9 text-slate-700">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export PDF
          </Button>
          <Button size="sm" onClick={() => setNewOpen(true)} data-testid="btn-new-record" className="h-9 bg-sky-700 hover:bg-sky-800">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Record
          </Button>
        </div>
      </div>

      {/* Cards + optional filter row — desktop only */}
      <div className="hidden lg:block px-6 pt-4 pb-2 bg-slate-50 border-b border-slate-200 shrink-0">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {CARDS.map((c) => (
            <Card
              key={c.key} card={c}
              value={summary?.[c.key]}
              active={activeCard === c.key}
              onClick={() => handleCardClick(c)}
            />
          ))}
        </div>

        {showFilters && (
          <div className="mt-3 p-3 rounded-lg border border-slate-200 bg-white">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <FilterInput type="date" label="Date from" value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} testid="filter-date-from" />
              <FilterInput type="date" label="Date to" value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })} testid="filter-date-to" />
              <FilterSelect label="Site" value={filters.site_id} onChange={(v) => setFilters({ ...filters, site_id: v })} options={sites.map((s) => ({ value: s.id, label: s.name }))} testid="filter-site" />
              <FilterSelect label="Category" value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} options={CATEGORIES.map((c) => ({ value: c, label: humanize(c) }))} testid="filter-category" />
              <FilterSelect label="Severity" value={filters.severity} onChange={(v) => setFilters({ ...filters, severity: v })} options={SEVERITIES.map((c) => ({ value: c, label: humanize(c) }))} testid="filter-severity" />
              <FilterSelect label="Status" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} options={STATUSES.map((c) => ({ value: c, label: humanize(c) }))} testid="filter-status" />
              <FilterSelect label="Safeguarding" value={filters.safeguarding} onChange={(v) => setFilters({ ...filters, safeguarding: v })} options={SAFEGUARDING.map((c) => ({ value: c, label: humanize(c) }))} testid="filter-safeguarding" />
              <FilterSelect label="Service User" value={filters.client_id} onChange={(v) => setFilters({ ...filters, client_id: v })} options={clients.map((c) => ({ value: c.id, label: c.full_name || c.name || c.id }))} testid="filter-client" />
            </div>
            <div className="mt-2 flex justify-end">
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="btn-clear-filters" className="h-7 text-xs">Clear filters</Button>
            </div>
          </div>
        )}
      </div>

      {/* Table + slide-out — desktop only */}
      <div className="hidden lg:flex flex-1 min-h-0 overflow-hidden">
        <div className="flex-1 min-w-0 overflow-auto bg-white">
          {loading ? (
            <div className="p-8 text-sm text-slate-500" data-testid="incidents-loading">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center">
              <div className="text-sm text-slate-500 mb-3">No incidents match the current filters.</div>
              {activeFilterCount > 0 && <Button size="sm" variant="outline" onClick={clearFilters}>Clear filters</Button>}
            </div>
          ) : (
            <table className="data-table" data-testid="incidents-table">
              <thead>
                <tr>
                  <th className="w-24">Ref</th>
                  <th>Date/Time</th>
                  <th>Category</th>
                  <th>Service User</th>
                  <th>Site/Location</th>
                  <th>Severity</th>
                  <th>Safeguarding</th>
                  <th>Reported By</th>
                  <th>Status</th>
                  <th className="w-20 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const overdue = (r.actions || []).some((a) => a.status === "overdue");
                  const siteName = sites.find((s) => s.id === r.site_id)?.name;
                  return (
                    <tr
                      key={r.id}
                      onClick={() => setSelected(r)}
                      className={selected?.id === r.id ? "bg-sky-50" : ""}
                      data-testid={`incident-row-${r.id}`}
                    >
                      <td className="mono text-[11px]">{r.reference}</td>
                      <td>{fmtDate(r.occurred_at)}</td>
                      <td className="capitalize">{humanize(r.category)}</td>
                      <td>{r.client_name || <span className="text-slate-300">—</span>}</td>
                      <td>{[siteName, r.location].filter(Boolean).join(" · ") || <span className="text-slate-300">—</span>}</td>
                      <td><StatusPill value={r.severity} kind="severity" /></td>
                      <td><StatusPill value={r.safeguarding_status} kind="generic" /></td>
                      <td>{r.reported_by || r.created_by_name}</td>
                      <td>
                        <StatusPill value={r.status} kind="generic" />
                        {overdue && <span className="ml-1 pill pill-danger">overdue action</span>}
                      </td>
                      <td className="text-right whitespace-nowrap">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/incidents/${r.id}`); }}
                          className="p-1 text-slate-500 hover:text-sky-700"
                          data-testid={`btn-open-full-${r.id}`}
                          title="Open full record"
                        ><Eye className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <QuickPanel
          incident={selected}
          onClose={() => setSelected(null)}
          onViewFull={() => navigate(`/incidents/${selected.id}`)}
        />
      </div>

      <IncidentNewDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={(created) => {
          setNewOpen(false);
          loadSummary();
          loadRows();
          toast.success(`Incident ${created.reference} recorded`);
          navigate(`/incidents/${created.id}`);
        }}
        clients={clients}
      />
    </div>
  );
}

function FilterInput({ type, label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-8 text-sm mt-0.5 bg-white" />
    </label>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-0.5 w-full h-8 text-sm border border-slate-300 rounded-md bg-white px-2 capitalize"
      >
        <option value="">All</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
